import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, BarChart3, TrendingUp, Users, Target, Download, Moon, Sun, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#ef4444', '#eab308', '#22c55e', '#3b82f6', '#a855f7'];

const ReportesDocente = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  
  const [grupos, setGrupos] = useState([]);
  const [ras, setRas] = useState([]);
  const [tendencias, setTendencias] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [selectedRA, setSelectedRA] = useState(null);
  const [reporteGrupo, setReporteGrupo] = useState(null);
  const [reporteRA, setReporteRA] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gruposRes, rasRes, tendenciasRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/grupos`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/resultados-aprendizaje`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reportes/tendencias`, { withCredentials: true })
      ]);
      
      setGrupos(gruposRes.data);
      setRas(rasRes.data);
      setTendencias(tendenciasRes.data.tendencias || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchReporteGrupo = async (grupoId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/reportes/grupo/${grupoId}`,
        { withCredentials: true }
      );
      setReporteGrupo(data);
    } catch (error) {
      console.error('Error fetching grupo report:', error);
      toast.error('Error al cargar reporte de grupo');
    }
  };

  const fetchReporteRA = async (raId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/reportes/ra/${raId}`,
        { withCredentials: true }
      );
      setReporteRA(data);
    } catch (error) {
      console.error('Error fetching RA report:', error);
      toast.error('Error al cargar reporte de RA');
    }
  };

  const handleExportarPDF = () => {
    toast.info('Exportación a PDF disponible próximamente');
  };

  const handleExportarExcel = () => {
    toast.info('Exportación a Excel disponible próximamente');
  };

  const handleLogout = async () => {
    navigate('/login');
  };

  const prepareLogrosData = (distribucion) => {
    return [
      { name: 'Inicial', value: distribucion.Inicial || 0, color: '#ef4444' },
      { name: 'Intermedio', value: distribucion.Intermedio || 0, color: '#eab308' },
      { name: 'Avanzado', value: distribucion.Avanzado || 0, color: '#22c55e' }
    ];
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/docente')}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Reportes y Análisis</h1>
                <p className="text-sm text-muted-foreground">Visualiza el progreso y rendimiento académico</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-button"
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="tendencias" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="tendencias" data-testid="tab-tendencias">
              <TrendingUp className="h-4 w-4 mr-2" />
              Tendencias
            </TabsTrigger>
            <TabsTrigger value="grupos" data-testid="tab-grupos">
              <Users className="h-4 w-4 mr-2" />
              Por Grupo
            </TabsTrigger>
            <TabsTrigger value="ras" data-testid="tab-ras">
              <Target className="h-4 w-4 mr-2" />
              Por RA
            </TabsTrigger>
          </TabsList>

          {/* TENDENCIAS */}
          <TabsContent value="tendencias">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Tendencias de Rendimiento</CardTitle>
                    <CardDescription>Evolución del promedio de calificaciones en el tiempo</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleExportarPDF}>
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExportarExcel}>
                      <Download className="h-4 w-4 mr-2" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary mx-auto"></div>
                  </div>
                ) : tendencias.length === 0 ? (
                  <p className="text-center text-muted-foreground py-16">No hay datos de tendencias disponibles</p>
                ) : (
                  <div className="space-y-6">
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={tendencias}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="promedio_puntaje" 
                          stroke="#3b82f6" 
                          strokeWidth={3}
                          name="Promedio %"
                        />
                      </LineChart>
                    </ResponsiveContainer>

                    <div className="grid grid-cols-2 gap-4">
                      <Card>
                        <CardHeader className="pb-3">
                          <CardDescription className="text-xs uppercase">Total Intentos</CardDescription>
                          <CardTitle className="text-3xl">{tendencias.reduce((sum, t) => sum + t.total_intentos, 0)}</CardTitle>
                        </CardHeader>
                      </Card>
                      <Card>
                        <CardHeader className="pb-3">
                          <CardDescription className="text-xs uppercase">Promedio General</CardDescription>
                          <CardTitle className="text-3xl">
                            {tendencias.length > 0 
                              ? Math.round(tendencias.reduce((sum, t) => sum + t.promedio_puntaje, 0) / tendencias.length)
                              : 0}%
                          </CardTitle>
                        </CardHeader>
                      </Card>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* GRUPOS */}
          <TabsContent value="grupos">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Seleccionar Grupo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {grupos.map((grupo) => (
                      <Button
                        key={grupo.grupo_id}
                        variant={selectedGrupo === grupo.grupo_id ? "default" : "outline"}
                        className="w-full justify-start"
                        onClick={() => {
                          setSelectedGrupo(grupo.grupo_id);
                          fetchReporteGrupo(grupo.grupo_id);
                        }}
                        data-testid={`grupo-${grupo.grupo_id}`}
                      >
                        {grupo.nombre}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-2">
                {!reporteGrupo ? (
                  <Card className="border-2 border-dashed border-border">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <BarChart3 className="h-16 w-16 text-muted-foreground mb-4" />
                      <p className="text-lg font-medium">Selecciona un grupo</p>
                      <p className="text-muted-foreground text-center">Haz clic en un grupo para ver su reporte detallado</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>{reporteGrupo.grupo?.nombre}</CardTitle>
                        <CardDescription>
                          {reporteGrupo.total_estudiantes} estudiantes • Promedio: {reporteGrupo.promedio_grupo}%
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={prepareLogrosData(reporteGrupo.distribucion_logros)}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={(entry) => `${entry.name}: ${entry.value}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {prepareLogrosData(reporteGrupo.distribucion_logros).map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>

                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={reporteGrupo.estudiantes}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} />
                              <YAxis domain={[0, 100]} />
                              <Tooltip />
                              <Bar dataKey="promedio_puntaje" fill="#3b82f6" name="Promedio %" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Estudiantes del Grupo</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {reporteGrupo.estudiantes?.map((estudiante) => (
                            <div 
                              key={estudiante.estudiante_id}
                              className="flex items-center justify-between p-3 border border-border rounded-lg"
                            >
                              <div>
                                <p className="font-medium">{estudiante.nombre}</p>
                                <p className="text-sm text-muted-foreground">
                                  {estudiante.actividades_completadas} actividades completadas
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold">{estudiante.promedio_puntaje}%</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* RESULTADOS DE APRENDIZAJE */}
          <TabsContent value="ras">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Seleccionar RA</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {ras.map((ra) => (
                      <Button
                        key={ra.ra_id}
                        variant={selectedRA === ra.ra_id ? "default" : "outline"}
                        className="w-full justify-start text-left"
                        onClick={() => {
                          setSelectedRA(ra.ra_id);
                          fetchReporteRA(ra.ra_id);
                        }}
                        data-testid={`ra-${ra.ra_id}`}
                      >
                        <div className="truncate">
                          <p className="font-semibold">{ra.codigo}</p>
                          <p className="text-xs opacity-70 truncate">{ra.descripcion}</p>
                        </div>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="lg:col-span-2">
                {!reporteRA ? (
                  <Card className="border-2 border-dashed border-border">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <Target className="h-16 w-16 text-muted-foreground mb-4" />
                      <p className="text-lg font-medium">Selecciona un RA</p>
                      <p className="text-muted-foreground text-center">Haz clic en un Resultado de Aprendizaje para ver su cumplimiento</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>{reporteRA.ra?.codigo}</CardTitle>
                        <CardDescription>{reporteRA.ra?.descripcion}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <Card>
                            <CardHeader className="pb-3">
                              <CardDescription className="text-xs uppercase">Actividades</CardDescription>
                              <CardTitle className="text-3xl">{reporteRA.total_actividades}</CardTitle>
                            </CardHeader>
                          </Card>
                          <Card>
                            <CardHeader className="pb-3">
                              <CardDescription className="text-xs uppercase">Cumplimiento</CardDescription>
                              <CardTitle className="text-3xl">{reporteRA.porcentaje_cumplimiento}%</CardTitle>
                            </CardHeader>
                          </Card>
                          <Card>
                            <CardHeader className="pb-3">
                              <CardDescription className="text-xs uppercase">Promedio</CardDescription>
                              <CardTitle className="text-3xl">{reporteRA.promedio_puntaje}%</CardTitle>
                            </CardHeader>
                          </Card>
                        </div>

                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={prepareLogrosData(reporteRA.distribucion_logros)}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => `${entry.name}: ${entry.value}`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {prepareLogrosData(reporteRA.distribucion_logros).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReportesDocente;
